import type { FC } from "react";
import { useEffect } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { usePdpLink } from "@jti/magento";
import type { ApiMagentoPurchaseType } from "@jti/magento-types";
import { Form } from "@jti/ui/components/Form";

import { ProductCta } from "@product-details/components/ProductCta/ProductCta";
import type { ConsumablesMatrixData } from "@product-details/containers/ConsumablesProductDetailsContainer/ConsumablesProductDetailsContainer.types";
import { useProductData } from "@product-details/hooks/useProductData";
import { useConsumablesProductDetailsContext } from "@product-details/providers/ConsumablesProductDetailsProvider";
import { updateConsumablesMatrixQuantitySearchParam } from "@product-details/utils/updateConsumablesMatrixQuantitySearchParam";

import { ConsumablesFlavorSelectorSection } from "./components/ConsumablesFlavorSelectorSection";
import { ConsumablesFormSubmitButton } from "./components/ConsumablesFormSubmitButton";
import { ConsumablesPackagingFormatSection } from "./components/ConsumablesPackagingFormatSection";
import { ConsumablesPriceDropdown } from "./components/ConsumablesPriceDropdown";
import { ConsumablesPurchaseTypeSection } from "./components/ConsumablesPurchaseTypeSection";
import { ConsumablesQuantitySection } from "./components/ConsumablesQuantitySection";
import { ConsumablesStrengthSelectorSection } from "./components/ConsumablesStrengthSelectorSection";
import { useConsumablesForm } from "./hooks/useConsumablesForm";

import { type ConsumablesFormValues } from "./ConsumablesForm.types";

import styles from "./ConsumablesForm.module.scss";

export type ConsumablesFormProps = {
  isSubscriptionsEnabled?: boolean;
  isCartLoaded: boolean;
  customBuyCtaLabel?: string;
  isPriceDropdownEnabled?: boolean;
  priceDropdownPromotionalMessage?: string;
  priceDropdownPromotionalTooltip?: string;
};

const hasItemsForContentsType = (
  purchaseType: ApiMagentoPurchaseType,
  packagingFormatId: string,
  consumablesMatrixData: ConsumablesMatrixData
) => {
  const hasOneTimePurchaseItems = consumablesMatrixData.items.find(
    (item) => item.packagingFormatId === packagingFormatId && item.purchaseType === purchaseType
  );

  return !!hasOneTimePurchaseItems;
};

export const ConsumablesForm: FC<ConsumablesFormProps> = ({
  isSubscriptionsEnabled,
  isCartLoaded,
  customBuyCtaLabel,
  isPriceDropdownEnabled,
  priceDropdownPromotionalMessage,
  priceDropdownPromotionalTooltip,
}) => {
  let stepCount = 0;

  const { currentMatrixItem, consumablesProductDetailsComponentData } = useConsumablesProductDetailsContext();
  const { SHOP_URL, PDP_URL } = useConfig();
  const currentSticksURL = usePdpLink(currentMatrixItem.sku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });

  const {
    form,
    onPurchaseTypeChange,
    onPackagingFormatChange,
    onFlavorsChange,
    onStrengthChange,
    onQuantityChange,
    handleSubmit,
    isSubmitting,
    tierPrices,
  } = useConsumablesForm({ isPriceDropdownEnabled, isSubscriptionsEnabled });

  const purchaseInputFormValue = form.watch("purchaseType");
  const quantityInputValue = form.watch("quantity");
  const packagingFormatIdInputValue = form.watch("packagingFormatId");
  const currentPackagingFormat = form.watch("packagingFormat");
  const selectedTierPrice = tierPrices.find((tierPrice) => tierPrice.quantity === Number(quantityInputValue));
  const selectedDiscountedTierPrice =
    selectedTierPrice?.discount?.percentOff && selectedTierPrice.totalPrice ? selectedTierPrice.totalPrice : undefined;

  const { consumablesMatrixData } = consumablesProductDetailsComponentData;

  const isRegulaPurchaseDisabled =
    purchaseInputFormValue === "subscription" &&
    !hasItemsForContentsType("regular", packagingFormatIdInputValue, consumablesMatrixData);

  const isSubscriptionUnavailable =
    isSubscriptionsEnabled &&
    (currentPackagingFormat?.contentsType === "pack" ||
      !hasItemsForContentsType("subscription", packagingFormatIdInputValue, consumablesMatrixData));

  const { isProductDataLoading, isProductDataLoaded, isInStock, stockStatus, finalPrices } = useProductData({
    sku: currentMatrixItem.sku,
    currentVariantSku: currentMatrixItem.sku,
  });

  useEffect(() => {
    updateConsumablesMatrixQuantitySearchParam(currentSticksURL, quantityInputValue);
  }, [currentSticksURL, quantityInputValue]);

  return (
    <Form<ConsumablesFormValues>
      form={form}
      onSubmit={handleSubmit}
      data-testid="consumablesMatrixData-form"
      className={styles.form}
    >
      {form.formState.errors?.root && <h1>{form.formState.errors.root.message}</h1>}

      {!!currentMatrixItem.strengthDetails && (
        <ConsumablesStrengthSelectorSection step={++stepCount} onChange={onStrengthChange} />
      )}

      {isSubscriptionsEnabled && (
        <ConsumablesPurchaseTypeSection
          onChange={onPurchaseTypeChange}
          groupName="purchaseType"
          step={++stepCount}
          value={purchaseInputFormValue}
          isOnetimePurchaseDisabled={isRegulaPurchaseDisabled}
          isSubscriptionUnavailable={isSubscriptionUnavailable}
        />
      )}

      <ConsumablesPackagingFormatSection
        groupName="packagingFormatId"
        consumables={consumablesMatrixData}
        onChange={onPackagingFormatChange}
        step={++stepCount}
      />

      <ConsumablesFlavorSelectorSection
        step={++stepCount}
        contentsType={currentPackagingFormat?.contentsType ?? "pack"}
        purchaseType={purchaseInputFormValue}
        shouldValidate={form.formState.errors.root?.type === "mixedBundleFlavorsMisconfiguration"}
        onFlavorChangeSubmit={onFlavorsChange}
      />

      {currentPackagingFormat &&
        (isPriceDropdownEnabled && tierPrices.length ? (
          <ConsumablesPriceDropdown
            step={++stepCount}
            tierPrices={tierPrices}
            regularPrice={currentMatrixItem.product.regularPrice}
            message={priceDropdownPromotionalMessage}
            tooltip={priceDropdownPromotionalTooltip}
          />
        ) : (
          <ConsumablesQuantitySection
            groupName="quantity"
            packagingFormat={currentPackagingFormat}
            step={++stepCount}
            onChange={onQuantityChange}
          />
        ))}

      {stockStatus === "AVAILABLE_IN_STORES" ? (
        <ProductCta
          productCta={consumablesProductDetailsComponentData.productCta}
          isCommerceDisabled={consumablesProductDetailsComponentData.isCommerceDisabled}
          isProductDataLoading={isProductDataLoading}
          stockStatus={stockStatus}
          unavailableStatuses={["AVAILABLE_IN_STORES"]}
        />
      ) : (
        <ConsumablesFormSubmitButton
          productName={currentMatrixItem.product.name}
          finalPrices={finalPrices}
          discountedPrice={selectedDiscountedTierPrice}
          quantity={+quantityInputValue}
          isProductDataLoading={isProductDataLoading}
          isProductDataLoaded={isProductDataLoaded}
          isAddProductInProgress={isSubmitting}
          isOutOfStockProductSelected={!isInStock}
          isCartLoaded={isCartLoaded}
          showPricePerMonthLabel={currentMatrixItem.purchaseType === "subscription"}
          customBuyCtaLabel={customBuyCtaLabel}
        />
      )}
    </Form>
  );
};
